// İSG uzmanı portalı — Eğitim & Test listesi (api-contract §16.1, tenant-scoped).
// `GET /trainings` (sayfalı + filtreli + arama). Tablo: başlık, **video rozeti** (hasVideo),
// soru sayısı, geçme eşiği, **durum rozeti** (Aktif/Pasif), oluşturulma. Filtreler: durum
// (aktif/pasif), arama. "Yeni Eğitim" birincil butonu editöre (`/trainings/new`) götürür; her
// satırda Düzenle (→ /trainings/{uuid}) + Sil (soft-delete, onay). Sıra backend'de oluşturulma
// azalan (istemci sort göndermez).

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Pagination,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  Tooltip,
  type TrainingDto,
  currentCulture,
  resolveApiErrorMessage,
  trainingApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Plus, SearchX, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { activeTagColor } from '../../components/status/statusTagColors';
import { formatDate } from '../../lib/format';
import { trainingKeys } from '../../lib/trainingKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

// S34-1 A6 — `passThreshold` normalde backend doğrulamasıyla `1..questionCount` aralığında
// tutulur; aralık dışı bir değer yalnız elle SQL/dump ile yazılmış bozuk bir dev satırında
// görülür (bkz. docs/BACKLOG.md). `questionCount` 0 ise HER pozitif eşik de aralık dışıdır.
function isThresholdOutOfRange(training: TrainingDto): boolean {
  return training.passThreshold < 1 || training.passThreshold > training.questionCount;
}

export function TrainingsListPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState<TrainingDto | null>(null);

  const activeOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('trainings.list.filter.allStatuses') },
      { value: 'true', label: t('trainings.status.active') },
      { value: 'false', label: t('trainings.status.inactive') },
    ],
    [t],
  );

  const params = {
    search: debouncedSearch,
    isActive: activeFilter === '' ? undefined : activeFilter === 'true',
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: trainingKeys.listParams(params),
    queryFn: () => trainingApi.listTrainings(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => trainingApi.deleteTraining(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.list() });
      queryClient.invalidateQueries({ queryKey: trainingKeys.access() });
      setToDelete(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || activeFilter);
  function clearFilters() {
    setSearch('');
    setActiveFilter('');
    setPage(1);
  }

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('trainings.title') }]} />
      <PageHeader
        title={t('trainings.title')}
        helperText={t('trainings.list.subtitle')}
        actions={
          <Button onClick={() => navigate('/trainings/new')}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('trainings.list.new')}
          </Button>
        }
      />

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('trainings.list.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('trainings.list.search')}
            searchPlaceholder={t('trainings.list.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('trainings.list.filter.status')}
                  placeholder={t('trainings.list.filter.allStatuses')}
                  options={activeOptions}
                  value={activeFilter}
                  onValueChange={resetPage(setActiveFilter)}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('trainings.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('trainings.list.col.title')}</TableHead>
                <TableHead>{t('trainings.list.col.video')}</TableHead>
                <TableHead className="text-right">{t('trainings.list.col.questions')}</TableHead>
                <TableHead className="text-right">
                  {t('trainings.list.col.passThreshold')}
                </TableHead>
                <TableHead>{t('trainings.list.col.status')}</TableHead>
                <TableHead>{t('trainings.list.col.created')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('trainings.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={7} />
              ) : query.isError ? (
                <TableMessageRow colSpan={7}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={7}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('trainings.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={GraduationCap}
                      title={t('trainings.list.empty')}
                      description={t('trainings.list.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate('/trainings/new')}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('trainings.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((training) => (
                  <TableRow key={training.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = başlık hücresi (S33 Slice 4 QA fix, defekt #4) —
                          eskiden ayrı bir Kalem düğmesiydi; hedefi zaten gerçek bir rota
                          (`/trainings/{uuid}` düzenleyicisi), o yüzden `TableLink` (S34-1 A2). */}
                      <TableLink to={`/trainings/${training.uuid}`} className="block">
                        {training.title}
                      </TableLink>
                      {training.description ? (
                        <span className="block max-w-md truncate text-xs text-muted">
                          {training.description}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {training.hasVideo ? (
                        <Tag color="blue">{t('trainings.list.video.yes')}</Tag>
                      ) : (
                        <span className="text-xs text-muted">{t('trainings.list.video.no')}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {training.questionCount}
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {/* S33-8 Pass B (turkish-ohs-expert bulgusu) — çıplak `passThreshold`
                          sayısı bağlamsızdı (3 mü doğru sayısı mı, yüzde mi belli değildi).
                          `TrainingDto.questionCount` (api-contract §16) liste satırında HER
                          ZAMAN dolu bir `number`; `<= 0` yalnız aktif sorusu olmayan bozuk bir
                          kayıtta olur — o durumda "3/0" anlamsız olurdu, tek doğru sayı basılır. */}
                      {/* S34-1 A6 (design review §5.4) — bazı dev satırlarında `passThreshold`
                          soru sayısını aşıyor (backend doğrulaması bunu normalde reddeder; bkz.
                          docs/BACKLOG.md) — ham değeri gizlemek yerine basıp yanına bir uyarı
                          işareti koyuyoruz; yeni renk yok, ikon `text-muted` kalır (Tooltip
                          varsayılanı), `label` koşulu kendisi anlatır (bkz. Tooltip.tsx başlığı). */}
                      <span className="inline-flex items-center justify-end gap-1">
                        {training.questionCount > 0
                          ? t('trainings.list.passThreshold.ratio', {
                              correct: training.passThreshold,
                              total: training.questionCount,
                            })
                          : t('trainings.list.passThreshold.correctOnly', {
                              correct: training.passThreshold,
                            })}
                        {isThresholdOutOfRange(training) ? (
                          <Tooltip
                            label={t('trainings.list.passThreshold.warning.label')}
                            content={t('trainings.list.passThreshold.warning.content')}
                            icon={TriangleAlert}
                          />
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tag color={activeTagColor(training.isActive)}>
                        {t(
                          training.isActive
                            ? 'trainings.status.active'
                            : 'trainings.status.inactive',
                        )}
                      </Tag>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDate(training.createdAt, culture)}
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('trainings.list.action.openMenu', {
                          name: training.title,
                        })}
                        items={[
                          {
                            key: 'delete',
                            label: t('trainings.list.action.delete'),
                            destructive: true,
                            onSelect: () => setToDelete(training),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('trainings.pagination.nav'),
                previous: t('trainings.pagination.previous'),
                next: t('trainings.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t('trainings.delete.title')}
        body={t('trainings.delete.body', { title: toDelete?.title ?? '' })}
        confirmLabel={t('trainings.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
